/**
 * Comparing the dotted version strings that gate content against builds.
 *
 * Three of them matter and they are all compared the same way: a catalog
 * entry's `minEngineVersion` against the running engine (task 14.5), a saved
 * run's engine version against the same (task 13.3), and a package's version
 * against the catalog's (task 13.5).
 *
 * Non-numeric segments sort as zero, which is deliberate rather than lazy. A
 * catalog is *content*, authored and published separately from the code that
 * reads it, and a typo in a `minEngineVersion` must not be able to lock a
 * player out of a mission they already own. Erring towards "playable" is the
 * right failure here; the validator is where a malformed version is caught.
 */

/** `-1`, `0` or `1`, comparing `left` to `right` segment by numeric segment. */
export function compareVersions(left: string, right: string): number {
  const a = left.split('.');
  const b = right.split('.');
  for (let index = 0; index < Math.max(a.length, b.length); index += 1) {
    const x = Number.parseInt(a[index] ?? '0', 10);
    const y = Number.parseInt(b[index] ?? '0', 10);
    const first = Number.isNaN(x) ? 0 : x;
    const second = Number.isNaN(y) ? 0 : y;
    if (first !== second) return first < second ? -1 : 1;
  }
  return 0;
}

/** True when `required` is newer than `running` — i.e. an update is needed. */
export function needsNewerThan(required: string, running: string): boolean {
  return compareVersions(required, running) > 0;
}
